import useCreateUrl from '@/api/mutations/file/useCreateURL';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DividerWithLabel,
  Field,
  FileDropZone,
  Form,
  InputGroup,
  Modal,
} from '@payaca/ambrosia';

import { getAcceptedFileTypes } from '@payaca/helpers/fileHelper';
import { requestPersistUploadAndLinkToEntity } from '@payaca/store/uploads/uploadsActions';
import { FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';

const acceptFileTypes = getAcceptedFileTypes([
  'document',
  'image',
  'video',
  'other',
]);

const externalLinkSchema = z.object({
  externalLink: z.string().url(),
});

interface Props {
  projectId: string;
  parentFolderId?: string;
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const DealFileUploadModalContent: FC<{
  projectInternalId: string;
  projectPublicId: string;
  parentFolderId?: string;
  onUploadSuccess: () => void;
}> = (props) => {
  const {
    projectInternalId,
    projectPublicId,
    parentFolderId,
    onUploadSuccess,
  } = props;

  const dispatch = useDispatch();

  const [showUploadError, setShowUploadError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const onUploadFile = useCallback(
    async (files: File[]) => {
      setShowUploadError(false);
      setIsLoading(true);

      const uploadedFilePromises = await Promise.allSettled(
        files.map(
          (file) =>
            new Promise<void>((resolve, reject) => {
              dispatch(
                requestPersistUploadAndLinkToEntity(
                  {
                    file,
                    fileName: file.name,
                    entityId: +projectInternalId,
                    entityType: 'deal',
                    parentFolderId,
                  },
                  (error) => {
                    if (error) {
                      reject();
                    } else {
                      resolve();
                    }
                  }
                )
              );
            })
        )
      );

      if (uploadedFilePromises.find((p) => p.status === 'rejected')) {
        setShowUploadError(true);
      }

      if (uploadedFilePromises.find((p) => p.status === 'fulfilled')) {
        onUploadSuccess();
      }

      setIsLoading(false);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'projectInternalId', and 'parentFolderId'
    [dispatch, onUploadSuccess]
  );

  const { mutateAsync: createURLMutation } = useCreateUrl();

  const handleAddLink = async ({ externalLink }: { externalLink: string }) => {
    await createURLMutation({
      url: externalLink,
      name: externalLink,
      linkedEntities: [
        {
          entityType: 'deal',
          entityId: projectPublicId,
          parentFolderId,
        },
      ],
    });

    onUploadSuccess();
  };

  const formMethods = useForm<{
    externalLink: string;
  }>({
    resolver: zodResolver(externalLinkSchema),
  });

  const { register } = formMethods;

  return (
    <>
      <FileDropZone
        isLoading={isLoading}
        hasError={showUploadError}
        dropzoneOptions={{
          onDrop: (files) => {
            void onUploadFile(files);
          },
          accept: acceptFileTypes.join(','),
        }}
      />

      <DividerWithLabel className="my-4" label="or" />

      <Form methods={formMethods} onSubmit={handleAddLink}>
        <Field name="externalLink">
          <Field.Label>Add external link</Field.Label>

          <InputGroup>
            <InputGroup.RawInput {...register('externalLink')} />
            <InputGroup.Button variant="white" type="submit">
              Add
            </InputGroup.Button>
          </InputGroup>
        </Field>
      </Form>
    </>
  );
};

export const DealFileUploadModal: FC<Props> = ({
  projectId,
  parentFolderId,
  isOpen,
  onClose,
  onUploadSuccess,
}) => {
  const { project, isLoading } = useGetProjectByIdOverview(projectId);

  return (
    <Modal title="Upload file" isOpen={isOpen} onClose={onClose}>
      <Modal.Body>
        {isLoading || !project ? (
          <>Loading...</>
        ) : (
          <DealFileUploadModalContent
            projectInternalId={project.id}
            projectPublicId={projectId}
            onUploadSuccess={onUploadSuccess}
            parentFolderId={parentFolderId}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};
